import React, { useState, useEffect, useRef } from 'react';
import {
    VStack,
    HStack,
    Pressable,
    View,
    Box,
    Badge,
} from 'native-base';
import { Caption, H3 } from '~/shared/Typography';
import { SpIcon } from '~/shared/Icon';
import {
    faChevronCircleDown,
    faChevronCircleUp,
    faGem,
    faCalendarCheck,
    faClock,
    faLocationDot,
    faReceipt,
} from '@fortawesome/free-solid-svg-icons';
import '~/shared/animations/animations.css';
import {
    I18nReacher,
    useGetHomePage,
    useI18n,
    useOffersScreen,
    useSpringBigStore,
    useStashboards,
    useTheme,
} from '@springbig/shared';
import { Subscriptions } from './components/Subscriptions';
import { GiftCards } from './components/GiftCards';
import { Referrals } from '~/pages/homePage/components/Referrals';
import { Milestones } from '~/pages/homePage/components/Milestones';
import { Messages } from '~/pages/homePage/components/Messages';
import useAnimate from '~/shared/animations/useAnimate';
import { ButtonsCard } from './components/ButtonsCard';
import { Rewards } from './components/Rewards';
import { Offers } from './components/Offers';
import { DesiredAction } from '../offer/components/OfferCard';
import { EditIcon } from './components/EditIcon';
import {
    Button,
    Section,
    ActionSheet,
    Card,
} from '~/shared/Theme';

export const HomePage = () => {

    const { fetch } = useStashboards();
    const { t } = useI18n();
    const { data } = useGetHomePage();

    const {
        member,
        merchant,
        stashboards,
    } = useSpringBigStore();

    const [tries, setTries] =
        useState(0);

    const [isOpenBonusRules, setIsOpenBonusRules] =
        useState<boolean>(false);

    const [isEditing, setIsEditing] =
        useState<boolean>(false);

    const [cards, setCards] =
        useState([]);

    useEffect(() => {

        if (merchant && member && !stashboards.length && tries < 3) {
            setTries(tries + 1);
            fetch();
        }
    }, [merchant, member, stashboards, tries]);

    const moveItemUp = (index: number): void => {

        if (index > 0 && index < cards.length) {
            const newCards = [...cards.filter(c => !!c)];
            const temp = newCards[index];
            newCards[index] = newCards[index - 1];
            newCards[index - 1] = temp;
            setCards(newCards);
        }
    };

    const moveItemDown = (index: number): void => {

        if (index >= 0 && index < cards.length - 1) {
            const newCards = [...cards.filter(c => !!c)];
            const temp = newCards[index];
            newCards[index] = newCards[index + 1];
            newCards[index + 1] = temp;
            setCards(newCards);
        }
    };

    const saveOrder = (): void => {

        try {
            const cardKeys = cards.map((card) => card && card.key);
            localStorage.setItem('cardsOrder', JSON.stringify(cardKeys));
            setIsEditing(false);
        }
        catch (error) {
            console.error('Failed to save order', error);
        }
    };

    const loadOrder = (_cards: Array<unknown>): void => {

        const storedOrder = localStorage.getItem('cardsOrder');
        if (storedOrder && JSON.parse(storedOrder).length === _cards.length) {
            const cardKeys = JSON.parse(storedOrder);
            const reorderedCards = cardKeys.map((key: string) =>

                _cards.find(card => card && card.key === key)
            );
            setCards(reorderedCards);
        }
        else {
            setCards(_cards);
        }
    };

    const { rewardGrants, desiredActions } = useOffersScreen();
    const initialCardsBuilt = useRef(false);

    useEffect(() => {

        if (merchant && member && data) {
            let offers = [];
            if (desiredActions?.length > 0) {
                offers = desiredActions?.filter((action: DesiredAction) =>

                    [8, 9, 10].includes(action.type_id),
                );
            }

            offers = [...offers, ...rewardGrants];
            const _cards = [
                ( data.milestones &&
                    <Milestones key={0} data={data.milestones} t={t} />
                ),
                ( data.rewards_offers &&
                    <ButtonsCard key={1} data={data.rewards_offers}/>
                ),
                ( data.gift_cards &&
                    <GiftCards key={2} data={data.gift_cards} />
                ),
                ( stashboards && stashboards.length > 0 &&
                  <Messages
                      key={3}
                      data={
                          [...stashboards].sort((a, b) => a.sent_at - b.sent_at).splice(0, 3)
                      }
                  />
                ),
                ( (data.rewards_offers.current_points !== null) && (data?.rewards_offers?.loyalty_enabled !== null) &&
                    <Rewards key={4} data={data.rewards_offers} />
                ),
                ( offers.length > 0 &&
                    <Offers key={5} offers={offers} t={t} />
                ),
                ( data.subscription_plans && data.subscription_plans.length > 0 &&
                    <Subscriptions key={6} t={t} />
                ),
                ( member.referrals.active && data.referrals &&
                    <Referrals key={7} data={data.referrals} t={t} />
                ),
            ];

            loadOrder(_cards.filter(Boolean));
            initialCardsBuilt.current = true;

        }
    }, [t, stashboards, data, merchant, member]);

    const { animation } = useAnimate(
        {
            keyframes: 'rock',
            duration: '.3s',
            originX: 'center',
            originY: 'top',
        }
    );

    const {
        radius_sm,
        radius_md,
        primary,
        highlight_text,
        button_primary,
        button_text,
        card,
        card_text,
    } = useTheme();

    const SubscriptionsPlans = () => {

        const subscriptionPlans = member?.subscription_plans;
        const activePlans = subscriptionPlans?.filter(
            (plan) => plan?.status === 'active' && plan?.subscription,
        );
        const activePlanName = activePlans[0]?.name;

        return (
            subscriptionPlans &&
            activePlans &&
            activePlans?.length > 0 && (
                <HStack
                    space={2}
                    pr={2}
                    alignItems={'center'}
                    backgroundColor={card}
                    mx={'auto'}
                    rounded={radius_md}
                >
                    <Badge
                        h={'28px'}
                        w={'28px'}
                        bg={button_primary}
                        borderRadius={radius_md}
                        alignItems={'center'}
                        justifyContent={'center'}
                    >
                        <SpIcon icon={faGem} color={button_text} />
                    </Badge>
                    <Caption color={card_text}>
                        { t('homepage.plan') }{' '}
                        { activePlans.length > 1 && activePlanName
                            ? `${activePlanName}, +${activePlans.length - 1} more`
                            : activePlanName
                        }
                    </Caption>
                </HStack>
            )
        );
    };

    const onClosePanel = (): void => {

        setIsOpenBonusRules(false);
    };

    const categories = {
        'day_of_the_week': faCalendarCheck,
        'order_source': faLocationDot,
        'transaction_location': faLocationDot,
        'time_of_day_range': faClock,
        'points_earned_range': faReceipt,
        'transaction_total_range': faReceipt,
        'product_purchased': faReceipt,
        'sku_purchased': faReceipt,
        'brand_purchased': faReceipt,
        'category_purchased': faReceipt,
    };

    const handleCondition = (type: string): string | undefined => {

        switch (type) {
        case 'single': return undefined;
        case 'and': return t('home_page.point_balance.and');
        case 'or': return t('home_page.point_balance.or');
        }
    };

    const handleValues = (type: string, values: Array<string>): string => {

        const types = {
            'day_of_the_week': [
                t((`home_page.point_balance.week_days[${values[0]}]`) as I18nReacher)
            ],
            'order_source': [
                t((`home_page.point_balance.locations[${values[0]}]`) as I18nReacher)
            ],
            'transaction_location': values,
            'time_of_day_range': values,
            'points_earned_range': values,
            'transaction_total_range': values,
            'sku_purchased': values,
            'product_purchased': values,
            'brand_purchased': values,
        };
        return types[type];
    };

    return (
        <Section py={4}>
            { member && merchant &&
                <>
                    <HStack justifyContent={'space-between'} w={'full'}>
                        <HStack justifyContent={'center'} flex={1}>
                            <Button
                                modifier={'unlocked'}
                                variant={'text'}
                                p={2}
                                onPress={() => (isEditing ? saveOrder() : setIsEditing(true))}
                            >
                                <View
                                    backgroundColor={button_text}
                                    borderColor={primary}
                                    borderWidth={1}
                                    px={0.5}
                                    py={0.5}
                                    rounded={radius_sm}
                                >
                                    <EditIcon color={primary} />
                                </View>
                                <Caption
                                    text={isEditing ? 'homepage.save' : 'homepage.edit'}
                                    color={button_primary}
                                    textAlign="center"
                                />
                            </Button>
                        </HStack>
                        <VStack flex={5} alignItems={'center'} pb={4}>
                            <H3
                                text={'homepage.welcomeBack'}
                                textValues={[member?.first_name ?? '']}
                            />
                            <Caption fontSize={'md'}>
                                { t('homepage.pointsAvailable')
                                    .split('{0}')
                                    .map((part, index, array) =>

                                        <React.Fragment key={index}>
                                            { part }
                                            { index !== array.length - 1 &&
                                                <Caption fontSize={'md'} fontWeight={'bold'}>
                                                    { data?.point_balance?.balance }
                                                </Caption>
                                            }
                                        </React.Fragment>
                                    )
                                }
                            </Caption>
                            {
                                data?.point_balance &&
                                data?.point_balance?.bonus_rules &&
                                data?.point_balance?.bonus_rules?.length > 0 &&
                                <Button
                                    onPress={() => setIsOpenBonusRules(true)}
                                    variant={'text'}
                                    modifier={'small'}
                                    text={'homepage.learnMore'}
                                />
                            }
                            <SubscriptionsPlans />
                        </VStack>
                        <Box flex={1}></Box>
                    </HStack>

                    <VStack space={4} px={2}>
                        {
                            data &&
                            cards.filter((_card) => !!_card).map((__card, index) =>

                                <HStack key={index}>
                                    <VStack
                                        space={4}
                                        backgroundColor={'transparent'}
                                        rounded={'2xl'}
                                        flex={1}
                                        style={isEditing ? animation : null}
                                    >
                                        { __card }
                                    </VStack>
                                    { isEditing &&
                                        <VStack space={4} pl={2} pt={2}>
                                            <Pressable onPress={() => moveItemUp(index)}>
                                                <SpIcon
                                                    size={20}
                                                    icon={faChevronCircleUp}
                                                    color={primary}
                                                />
                                            </Pressable>
                                            <Pressable onPress={() => moveItemDown(index)}>
                                                <SpIcon
                                                    size={20}
                                                    icon={faChevronCircleDown}
                                                    color={primary}
                                                />
                                            </Pressable>
                                        </VStack>
                                    }
                                </HStack>
                            )
                        }
                    </VStack>
                </>
            }
            { isOpenBonusRules &&
                <ActionSheet
                    isOpen={isOpenBonusRules}
                    onClose={onClosePanel}
                >
                    <VStack space={4} pb={4}>
                        <H3
                            text={'home_page.point_balance.bonus_points.title'}
                            alignSelf={'center'}
                            fontSize={20}
                            fontWeight={'400'}
                        />
                        { data?.point_balance?.bonus_rules?.map((rule, index) =>

                            <Card
                                secondary
                                row
                                key={index}
                                space={rule?.conditions?.length > 1 ? 2 : 0}
                                h={20}
                            >
                                <HStack
                                    space={1}
                                    justifyContent={'center'}
                                    alignItems={'center'}
                                    p={2}
                                    w={20}
                                    flex={1}
                                >
                                    { rule.conditions?.map((condition, condition_index) =>

                                        <Box
                                            key={condition_index}
                                            mt={rule.conditions.length > 1 && condition_index === 0 ? -5 : 0}
                                            mb={rule.conditions.length > 1 && condition_index > 0 ? -5 : 0}
                                            position={'relative'}
                                        >
                                            { rule.conditions.length > 1 && condition_index > 0 &&
                                                    <Caption
                                                        position={'absolute'}
                                                        left={0}
                                                        top={-18}
                                                        fontSize={12}
                                                        color={highlight_text}
                                                        opacity={.5}
                                                    >
                                                        { handleCondition(rule.condition_type) }
                                                    </Caption>
                                            }
                                            <SpIcon
                                                icon={categories[condition.type]}
                                                color={primary}
                                                size={rule.conditions.length > 1 ? 18 : 36}
                                            />
                                        </Box>
                                    )}
                                </HStack>
                                <Caption
                                    alignSelf={'center'}
                                    textAlign={'center'}
                                    p={4}
                                    pl={0}
                                    pr={rule?.conditions?.length > 1 ? 4 : 0}
                                    flex={5}
                                    color={highlight_text}
                                >
                                    <Caption fontSize={16}>
                                        { t('home_page.point_balance.bonus_rules.earning_prefix_multiplication', [rule.points]) + ' ' }
                                    </Caption>
                                    {
                                        rule.conditions.length > 0 &&
                                            rule.conditions.map((condition, condition_index) =>

                                                <Caption key={condition_index}>
                                                    <Caption fontSize={16}>
                                                        { t(
                                                            (`home_page.point_balance.bonus_rules[${condition.type}]`) as I18nReacher,
                                                            handleValues(condition.type, condition.values)
                                                        ) }
                                                    </Caption>
                                                    {
                                                        rule.human_readable_rule.length > 1 &&
                                                        condition_index < rule.conditions.length - 1 &&
                                                        <Caption fontSize={16} fontWeight={'700'}>
                                                            { ' ' + rule.condition_type + ' ' }
                                                        </Caption>
                                                    }
                                                </Caption>
                                            )}
                                </Caption>
                            </Card>
                        )}
                    </VStack>
                </ActionSheet>
            }
        </Section>
    );
};
